/**
 * Generic polling primitive.
 *
 * Drives a `fetcher` on an interval, with `isActive(data)` deciding
 * whether to schedule the next tick. Auto-cancels on unmount via
 * AbortController; auto-stops when `isActive` returns false (idles
 * until the caller invokes `refetch()`).
 *
 * Use cases:
 *   - `useWorkflowStatusPolling` (workflow-wide status snapshot)
 *   - Future: notification polling, version-history polling, etc.
 *
 * Contract:
 *   - `fetcher(signal)` should be a stateless async function. It
 *     receives an `AbortSignal` and is expected to forward it to
 *     the underlying request so cleanup cancels the in-flight call.
 *   - `isActive(data)` is invoked with the latest non-null fetcher
 *     return value. Truthy → schedule another tick. Falsy → idle.
 *   - `onData(data)` is the side-effect callback (typically a
 *     reducer dispatch).
 *   - Errors are surfaced via the returned `error` value AND the
 *     hook continues retrying at `intervalMs * 2` until the next
 *     successful tick (transient network failures shouldn't strand
 *     the consumer).
 *   - `refetch()` triggers an immediate tick, regardless of idle
 *     state. Used by consumers to wake polling after a user action
 *     (e.g. approve → re-hydrate after 500ms).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_INTERVAL_MS = 3000;

export interface UsePollingInput<TData> {
  fetcher: (signal: AbortSignal) => Promise<TData>;
  isActive?: (data: TData) => boolean;
  onData?: (data: TData) => void;
  intervalMs?: number;
  enabled?: boolean;
}

export interface PollingState {
  refetch: () => void;
  error: string | null;
}

export function usePolling<TData = unknown>({
  fetcher,
  isActive,
  onData,
  intervalMs = DEFAULT_INTERVAL_MS,
  enabled = true,
}: UsePollingInput<TData>): PollingState {
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  // tickKey forces the polling effect to re-run on demand (used by
  // refetch()). Plain dep change keeps cancel/start logic in one place.
  const [tickKey, setTickKey] = useState(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refetch = useCallback(() => {
    setTickKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!enabled || !fetcher) return undefined;

    let cancelled = false;
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    const tick = async (): Promise<void> => {
      try {
        const data = await fetcher(signal);
        if (cancelled || !mountedRef.current) return;
        setError(null);
        onData?.(data);
        if (isActive ? isActive(data) : true) {
          timerRef.current = setTimeout(() => {
            if (!cancelled && mountedRef.current) tick();
          }, intervalMs);
        }
      } catch (e: any) {
        if (e?.name === 'AbortError' || e?.code === 'ERR_CANCELED') return;
        if (cancelled || !mountedRef.current) return;
        setError(e?.message || 'Polling failed');
        // Transient retry — back off slightly to avoid hammering.
        timerRef.current = setTimeout(() => {
          if (!cancelled && mountedRef.current) tick();
        }, intervalMs * 2);
      }
    };

    tick();

    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, [enabled, fetcher, isActive, onData, intervalMs, tickKey]);

  return { refetch, error };
}
